import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mic } from "lucide-react";
import NexoCore from "./NexoCore";
import VoiceDebugPanel from "@/components/voice/VoiceDebugPanel";
import { cn } from "@/lib/utils";

// Fase E5 — overlay fullscreen imersivo ao ativar voz (estilo Siri iOS 18 /
// ChatGPT Voice). Gerenciado por NectarDashboard via prop isOpen.
//
// Sai por: botão X, tecla ESC, clique no backdrop, ou 3s após Nexo terminar.
// Interação por voz continua funcionando dentro do overlay.
//
// Refinamento visual:
// - Backdrop translúcido (60%) + blur-xl → app visível atrás, foco central
// - Expansão cinemática do orb (1.2s, expo-out) com 3 ondas de conexão
// - Stagger: backdrop → orb → status → X/hint (400/100+1200/800/1100ms)
// - Breathing glow loop no idle imersivo (sensação "vivo")

const EASE_EXPO_OUT = [0.16, 1, 0.3, 1];  // começa rápido, chegada suave
const EASE_SMOOTH   = [0.2,  0.8, 0.2, 1];

// Mesmo mapeamento do HeroSection pra consistência visual.
function resolveUiState(state, vadSpeaking) {
  if (state === "connecting") return "connecting";
  if (state === "listening" && vadSpeaking) return "user-speaking";
  if (state === "listening") return "armed";
  if (state === "processing") return "processing";
  if (state === "speaking") return "responding";
  return "idle";
}

// VOICE-UNIFICAR — overlay imersivo mostra APENAS um destes 3 labels.
// Sem "Conectando", sem transcript do user, sem greetingText.
// idle/armed/connecting/user-speaking colapsam em "Escutando" porque
// do ponto de vista do usuario sao todos "estou ouvindo".
const UI_CONFIG = {
  idle:            { label: "Escutando",   accent: "text-brand-cyan"   },
  connecting:      { label: "Escutando",   accent: "text-brand-cyan"   },
  armed:           { label: "Escutando",   accent: "text-brand-cyan"   },
  "user-speaking": { label: "Escutando",   accent: "text-brand-green"  },
  processing:      { label: "Processando", accent: "text-brand-purple" },
  responding:      { label: "Nexo falando", accent: "text-brand-cyan"  },
};

// ============ VARIANTS ============

const backdropVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4, ease: EASE_SMOOTH } },
  exit:    { opacity: 0, transition: { duration: 0.5, ease: EASE_SMOOTH } },
};

const orbVariants = {
  hidden:  { scale: 0.3, opacity: 0 },
  visible: {
    scale: 1, opacity: 1,
    transition: { delay: 0.1, duration: 1.2, ease: EASE_EXPO_OUT },
  },
  exit: {
    scale: 0.55, opacity: 0,
    transition: { duration: 0.6, ease: EASE_SMOOTH },
  },
};

const statusVariants = {
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { delay: 0.8, duration: 0.6, ease: EASE_SMOOTH } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.3 } },
};

const closeBtnVariants = {
  hidden:  { opacity: 0, scale: 0.75 },
  visible: { opacity: 1, scale: 1, transition: { delay: 1.1, duration: 0.4, ease: EASE_SMOOTH } },
  exit:    { opacity: 0, scale: 0.8, transition: { duration: 0.25 } },
};

const hintVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 0.55, transition: { delay: 1.1, duration: 0.4 } },
  exit:    { opacity: 0, transition: { duration: 0.2 } },
};

// Breathing glow: só ativa em idle/armed (antes do user falar) pra sinalizar
// "vivo, esperando você". Some em processing/responding pra não competir com
// a animação natural do NexoCore.
const breathingVariants = {
  active: {
    scale: [1, 1.05, 1],
    opacity: [0.35, 0.55, 0.35],
    transition: { duration: 3.2, repeat: Infinity, ease: "easeInOut" },
  },
  calm: { scale: 1, opacity: 0.35 },
};

// ============ CONNECTION WAVES ============

/**
 * Ondas concêntricas saindo do centro. 3 ondas staggered (0/200/400ms).
 * - continuous=false: one-shot na mount (pulso de abertura)
 * - continuous=true:  loop infinito com repeatDelay — usado durante a fase
 *   "connecting" pra reforçar a sensação de handshake rolando.
 */
function ConnectionWaves({ continuous = false }) {
  const base = continuous
    ? {
        duration: 2.2,
        repeat: Infinity,
        repeatDelay: 0.3,
        ease: EASE_EXPO_OUT,
      }
    : {
        duration: 1.8,
        ease: EASE_EXPO_OUT,
      };
  return (
    <div
      aria-hidden
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      style={{ width: 0, height: 0 }}
    >
      {[0, 0.25, 0.5].map((delay, i) => (
        <motion.span
          key={`${continuous ? "loop" : "once"}-${i}`}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
          style={{ borderColor: "rgba(0, 212, 255, 0.45)" }}
          initial={{ width: 280, height: 280, opacity: 0.6 }}
          animate={{ width: 1200, height: 1200, opacity: 0 }}
          transition={{ ...base, delay: 0.15 + delay }}
        />
      ))}
    </div>
  );
}


// ============ MAIN COMPONENT ============

export default function ImmersiveVoiceOverlay({
  isOpen,
  state,
  vadSpeaking,
  // VOICE-UNIFICAR — props mantidas pra compat com call sites (NectarDashboard
  // ainda passa). NAO sao usadas no label — overlay mostra apenas o texto
  // fixo do UI_CONFIG. transcript ainda é repassado ao debug panel.
  transcript,
  // eslint-disable-next-line no-unused-vars
  greetingText,
  onClose,
  // VOICE-DEBUG-PANEL — props pro painel visual de diagnóstico. Quando
  // ausentes, painel não renderiza (compatibilidade com callers antigos).
  debugProps,
}) {
  const containerRef = useRef(null);

  // ESC fecha
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Lock body scroll enquanto overlay aberto
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const uiState = resolveUiState(state, vadSpeaking);
  const cfg = UI_CONFIG[uiState] || UI_CONFIG.idle;
  const isConnecting = uiState === "connecting";
  const isIdleLike = uiState === "idle" || uiState === "armed";

  // VOICE-UNIFICAR — overlay mostra SOMENTE o label fixo do UI_CONFIG.
  // Sem transcript, sem greetingText, sem "Conectando" especial.
  const displayText = cfg.label;

  const handleBackdropClick = (e) => {
    // Fecha só se clicou DIRETO no backdrop, não num filho.
    if (e.target === containerRef.current) onClose?.();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={containerRef}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={handleBackdropClick}
          // bg 60% + blur-xl + vinheta radial = app visível atrás mas foco no centro.
          // No-backdrop-filter fallback via bg/80 aplicado via media query CSS puro
          // seria mais robusto; pra agora confiamos no supports do Chrome/Safari recentes.
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-surface-canvas/60 backdrop-blur-xl"
          style={{
            // Vinheta radial sutil — foco visual no centro sem escurecer bordas.
            backgroundImage:
              "radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.45) 100%)",
          }}
          data-testid="immersive-voice-overlay"
          data-ui-state={uiState}
        >
          {/* Close button — aparece no final do stagger */}
          <motion.button
            type="button"
            onClick={onClose}
            variants={closeBtnVariants}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-surface-card/80 border border-subtle text-foreground-muted hover:text-foreground hover:border-strong backdrop-blur-sm flex items-center justify-center z-10"
            data-testid="immersive-close"
            aria-label="Fechar modo voz"
          >
            <X className="w-5 h-5" />
          </motion.button>

          {/* Status text ACIMA do orb */}
          <motion.div
            variants={statusVariants}
            className="max-w-2xl px-6 text-center mb-10 pointer-events-none"
          >
            <AnimatePresence mode="wait">
              <motion.p
                key={displayText + uiState}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: EASE_SMOOTH }}
                className={cn(
                  "font-display text-h2 md:text-hero leading-tight inline-flex items-baseline justify-center",
                  uiState === "responding" ? "text-foreground" : cfg.accent,
                )}
                style={{ letterSpacing: "-0.01em" }}
              >
                {displayText}
              </motion.p>
            </AnimatePresence>
          </motion.div>

          {/* Orb grande centralizado + ondas de conexão + breathing glow */}
          <motion.div
            variants={orbVariants}
            className="relative pointer-events-none"
          >
            {/* Ondas de conexão:
                - always one-shot no mount (pulso cinemático de abertura)
                - durante fase "connecting", uma 2ª camada em loop reforça
                  a sensação de handshake rolando. Some quando passa pra
                  "armed"/"listening". */}
            <ConnectionWaves key="once" continuous={false} />
            {isConnecting && <ConnectionWaves key="loop" continuous={true} />}

            {/* Breathing glow — loop em idle/armed/connecting.
                Para em processing/responding pra não competir com o
                NexoCore. Key por fase pra reiniciar animação limpa. */}
            <motion.div
              key={`breath-${isIdleLike || isConnecting ? "breathing" : "calm"}`}
              variants={breathingVariants}
              animate={isIdleLike || isConnecting ? "active" : "calm"}
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(0,212,255,0.28) 0%, transparent 70%)",
                filter: "blur(40px)",
              }}
            />

            <NexoCore state={state} size={560} />
          </motion.div>

          {/* Hint de fechar — mesmo timing do botão X */}
          <motion.div
            variants={hintVariants}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-caption text-foreground-subtle pointer-events-none"
          >
            <Mic className="w-3.5 h-3.5" />
            <span>ESC ou clique fora pra sair</span>
          </motion.div>

          {/* VOICE-DEBUG-PANEL — diagnóstico visual em fase beta. Sempre
              renderizado quando debugProps presente; painel se esconde via
              botão X interno. Pós-bug fechado vira condicional sob
              REACT_APP_VOICE_DEBUG. */}
          {debugProps && (
            <VoiceDebugPanel
              voiceState={state}
              vadSpeaking={vadSpeaking}
              transcript={transcript}
              prewarmState={debugProps.prewarmState}
              isOutputSpeaker={debugProps.isOutputSpeaker}
              debugSnapshot={debugProps.debugSnapshot}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
